/**
 * Route Configuration — Centralized lazy-loaded route definitions.
 *
 * Each route specifies a path, a lazy-loaded component, and an optional guard type.
 * Adding a new route is a single line — no boilerplate wrapping needed.
 */

import type { ComponentType } from 'react';
import React from 'react';

// --- Types ---

export type RouteGuard = 'public' | 'protected' | 'admin';

export interface AppRouteConfig {
  /** URL path (e.g. "/dashboard") */
  path: string;
  /** Lazy-loaded page component */
  component: React.LazyExoticComponent<ComponentType>;
  /** Which guard to wrap the route with (default: 'protected') */
  guard: RouteGuard;
}

// --- Lazy page imports ---

const DashboardPage = React.lazy(() =>
  import('@features/dashboard/pages/DashboardPage').then((m) => ({ default: m.DashboardPage }))
);
const TopicsPage = React.lazy(() =>
  import('@features/topics/pages/TopicsPage').then((m) => ({ default: m.TopicsPage }))
);
const QuizPage = React.lazy(() =>
  import('@features/quiz/pages/QuizPage').then((m) => ({ default: m.QuizPage }))
);
const LessonPage = React.lazy(() =>
  import('@features/lessons/pages/LessonPage').then((m) => ({ default: m.LessonPage }))
);
const LessonsListPage = React.lazy(() =>
  import('@features/lessons/pages/LessonsListPage').then((m) => ({ default: m.LessonsListPage }))
);
const ProfilePage = React.lazy(() =>
  import('@features/profile/pages/ProfilePage').then((m) => ({ default: m.ProfilePage }))
);
const BadgesPage = React.lazy(() =>
  import('@features/badges/pages/BadgesPage').then((m) => ({ default: m.BadgesPage }))
);
const LeaderboardPage = React.lazy(() =>
  import('@features/leaderboard/pages/LeaderboardPage').then((m) => ({
    default: m.LeaderboardPage,
  }))
);
const DailyChallengePage = React.lazy(() =>
  import('@features/daily-challenge/pages/DailyChallengePage').then((m) => ({
    default: m.DailyChallengePage,
  }))
);
const FriendsPage = React.lazy(() =>
  import('@features/friends/pages/FriendsPage').then((m) => ({ default: m.FriendsPage }))
);
const DuelsPage = React.lazy(() =>
  import('@features/duels/pages/DuelsPage').then((m) => ({ default: m.DuelsPage }))
);
const NotificationsPage = React.lazy(() =>
  import('@features/notifications/pages/NotificationsPage').then((m) => ({
    default: m.NotificationsPage,
  }))
);
const EventsPage = React.lazy(() =>
  import('@features/events/pages/EventsPage').then((m) => ({ default: m.EventsPage }))
);
const ParentDashboardPage = React.lazy(() =>
  import('@features/parent/pages/ParentDashboardPage').then((m) => ({
    default: m.ParentDashboardPage,
  }))
);
const ClassroomPage = React.lazy(() =>
  import('@features/classroom/pages/ClassroomPage').then((m) => ({ default: m.ClassroomPage }))
);
const ReviewPage = React.lazy(() =>
  import('@features/review/pages/ReviewPage').then((m) => ({ default: m.ReviewPage }))
);

// Admin pages
const AdminDashboardPage = React.lazy(() =>
  import('@features/admin/pages/AdminDashboardPage').then((m) => ({
    default: m.AdminDashboardPage,
  }))
);
const TopicsManagementPage = React.lazy(() =>
  import('@features/admin/pages/TopicsManagementPage').then((m) => ({
    default: m.TopicsManagementPage,
  }))
);
const QuestionsManagementPage = React.lazy(() =>
  import('@features/admin/pages/QuestionsManagementPage').then((m) => ({
    default: m.QuestionsManagementPage,
  }))
);
const BadgesManagementPage = React.lazy(() =>
  import('@features/admin/pages/BadgesManagementPage').then((m) => ({
    default: m.BadgesManagementPage,
  }))
);
const LessonsManagementPage = React.lazy(() =>
  import('@features/admin/pages/LessonsManagementPage').then((m) => ({
    default: m.LessonsManagementPage,
  }))
);
const LevelsManagementPage = React.lazy(() =>
  import('@features/admin/pages/LevelsManagementPage').then((m) => ({
    default: m.LevelsManagementPage,
  }))
);
const AdminUsersPage = React.lazy(() =>
  import('@features/admin/pages/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage }))
);
const SettingsPage = React.lazy(() =>
  import('@features/admin/pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);

// --- Route definitions ---

/**
 * Protected routes — require authentication, wrapped in Layout.
 */
export const protectedRoutes: AppRouteConfig[] = [
  { path: '/dashboard', component: DashboardPage, guard: 'protected' },
  { path: '/topics', component: TopicsPage, guard: 'protected' },
  { path: '/topics/:topicId/lessons', component: LessonsListPage, guard: 'protected' },
  { path: '/quiz/:topicId', component: QuizPage, guard: 'protected' },
  { path: '/lesson/:topicId/:subSkillId', component: LessonPage, guard: 'protected' },
  { path: '/lesson/:topicId', component: LessonPage, guard: 'protected' },
  { path: '/profile', component: ProfilePage, guard: 'protected' },
  { path: '/badges', component: BadgesPage, guard: 'protected' },
  { path: '/leaderboard', component: LeaderboardPage, guard: 'protected' },
  { path: '/daily-challenge', component: DailyChallengePage, guard: 'protected' },
  { path: '/friends', component: FriendsPage, guard: 'protected' },
  { path: '/duels', component: DuelsPage, guard: 'protected' },
  { path: '/notifications', component: NotificationsPage, guard: 'protected' },
  { path: '/events', component: EventsPage, guard: 'protected' },
  { path: '/events/:eventId', component: EventsPage, guard: 'protected' },
  { path: '/parent', component: ParentDashboardPage, guard: 'protected' },
  { path: '/classroom', component: ClassroomPage, guard: 'protected' },
  { path: '/review', component: ReviewPage, guard: 'protected' },
];

/**
 * Admin routes — require authentication + admin role, no Layout wrapper.
 */
export const adminRoutes: AppRouteConfig[] = [
  { path: '/admin', component: AdminDashboardPage, guard: 'admin' },
  { path: '/admin/topics', component: TopicsManagementPage, guard: 'admin' },
  { path: '/admin/questions', component: QuestionsManagementPage, guard: 'admin' },
  { path: '/admin/lessons', component: LessonsManagementPage, guard: 'admin' },
  { path: '/admin/badges', component: BadgesManagementPage, guard: 'admin' },
  { path: '/admin/levels', component: LevelsManagementPage, guard: 'admin' },
  { path: '/admin/users', component: AdminUsersPage, guard: 'admin' },
  { path: '/admin/settings', component: SettingsPage, guard: 'admin' },
];
